import React, { useState, useEffect } from 'react';
import {
  TagIcon,
  TrophyIcon,
  ChartBarIcon,
  CalendarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  FireIcon,
  CubeIcon,
  StarIcon,
  CurrencyDollarIcon,
  AdjustmentsHorizontalIcon,
  SparklesIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { Line, Bar } from 'react-chartjs-2';
import { nutritionApi, NutritionalTrendData } from '../api/nutrition';
import { format, subDays, isWithinInterval } from 'date-fns';
import { useFormatting } from '../hooks/useFormatting';
import DecimalInput from './ui/DecimalInput';
import { ModalCloseButton } from './ui/BrandModal';

interface NutritionalGoal {
  id: string;
  name: string;
  description?: string;
  category: 'protein' | 'fat' | 'fiber' | 'moisture' | 'calcium' | 'phosphorus' | 'magnesium' | 'potassium' | 'sodium' | 'iron' | 'zinc' | 'manganese' | 'copper' | 'selenium' | 'vitaminA' | 'vitaminD3' | 'vitaminE' | 'vitaminK' | 'thiamine' | 'riboflavin' | 'niacin' | 'pantothenicAcid' | 'pyridoxine' | 'biotin' | 'folicAcid' | 'vitaminB12' | 'choline' | 'quality' | 'cost' | 'custom';
  targetValue: number;
  currentValue: number;
  unit: string;
  targetDate: string;
  priority: 'high' | 'medium' | 'low';
  status: 'active' | 'achieved' | 'overdue' | 'paused';
  progress: number; // 0-100
  locations?: string[];
  createdAt: string;
  updatedAt: string;
  metadata?: {
    baseline?: number;
    milestones?: Array<{
      date: string;
      value: number;
      note?: string;
    }>;
    tags?: string[];
    targetRange?: {
      min: number;
      max: number;
      optimal: number;
    };
  };
}

interface NutritionalGoalTrackerProps {
  selectedLocation?: string;
  onGoalUpdate?: () => void;
}

export default function NutritionalGoalTracker({
  selectedLocation,
  onGoalUpdate
}: NutritionalGoalTrackerProps) {
  const { formatCurrency, formatDate, getCurrencySymbol } = useFormatting();
  const [goals, setGoals] = useState<NutritionalGoal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<NutritionalGoal | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'achieved' | 'overdue'>('all');
  const [trendData, setTrendData] = useState<NutritionalTrendData[]>([]);
  const [editGoal, setEditGoal] = useState<NutritionalGoal | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteGoal, setDeleteGoal] = useState<NutritionalGoal | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Create goal form state
  const [newGoal, setNewGoal] = useState({
    name: '',
    description: '',
    category: 'protein' as const,
    targetValue: 0,
    unit: '%',
    targetDate: '',
    priority: 'medium' as const,
    locations: [] as string[]
  });

  useEffect(() => {
    loadGoals();
    loadTrendData();
  }, [selectedLocation]);

  const loadGoals = async () => {
    // Mock goals data - in real implementation, this would be an API call
    const mockGoals: NutritionalGoal[] = [
      {
        id: '1',
        name: 'Increase Protein Content',
        description: 'Achieve consistent 16-18% protein in daily mixes',
        category: 'protein',
        targetValue: 17,
        currentValue: 14.2,
        unit: '%',
        targetDate: '2024-03-01',
        priority: 'high',
        status: 'active',
        progress: 65,
        locations: selectedLocation ? [selectedLocation] : ['Main Aviary'],
        createdAt: '2024-01-15',
        updatedAt: '2024-01-25',
        metadata: {
          baseline: 12.5,
          milestones: [
            { date: '2024-01-20', value: 13.1, note: 'Added high-protein pellets' },
            { date: '2024-01-25', value: 14.2, note: 'Optimized feed mix ratios' }
          ],
          tags: ['breeding-season', 'nutrition-improvement']
        }
      },
      {
        id: '2',
        name: 'Reduce Feed Costs',
        description: `Lower daily feed cost per bird to under ${getCurrencySymbol()}0.50`,
        category: 'cost',
        targetValue: 0.50,
        currentValue: 0.62,
        unit: `${getCurrencySymbol()}/bird`,
        targetDate: '2024-02-15',
        priority: 'medium',
        status: 'active',
        progress: 45,
        locations: ['Main Aviary', 'Breeding Area'],
        createdAt: '2024-01-10',
        updatedAt: '2024-01-25',
        metadata: {
          baseline: 0.75,
          milestones: [
            { date: '2024-01-15', value: 0.68, note: 'Negotiated better supplier rates' },
            { date: '2024-01-22', value: 0.62, note: 'Optimized feed mix composition' }
          ],
          tags: ['cost-reduction', 'efficiency']
        }
      },
      {
        id: '3',
        name: 'Improve Quality Score',
        description: 'Maintain average quality score above 85',
        category: 'quality',
        targetValue: 85,
        currentValue: 87,
        unit: '/100',
        targetDate: '2024-04-01',
        priority: 'medium',
        status: 'achieved',
        progress: 100,
        locations: selectedLocation ? [selectedLocation] : ['Main Aviary'],
        createdAt: '2024-01-01',
        updatedAt: '2024-01-20',
        metadata: {
          baseline: 78,
          milestones: [
            { date: '2024-01-10', value: 82, note: 'Improved feed component balance' },
            { date: '2024-01-20', value: 87, note: 'Achieved target quality score' }
          ],
          tags: ['quality-improvement', 'nutrition-balance']
        }
      }
    ];

    setGoals(mockGoals);
  };

  const loadTrendData = async () => {
    try {
      const endDate = new Date();
      const startDate = subDays(endDate, 30);

      const trends = await nutritionApi.getNutritionalTrends({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        location: selectedLocation
      });

      setTrendData(trends.data);
    } catch (error) {
      console.error('Failed to load trend data:', error);
    }
  };

  const updateGoalProgress = (goal: NutritionalGoal, trendData: NutritionalTrendData[]): { currentValue: number; progress: number } => {
    if (trendData.length === 0) return { currentValue: goal.currentValue, progress: goal.progress };

    const latest = trendData[trendData.length - 1];
    let currentValue = goal.currentValue;
    
    switch (goal.category) {
      // Macronutrients
      case 'protein':
        currentValue = latest.proteinPercentage;
        break;
      case 'fat':
        currentValue = latest.fatPercentage;
        break;
      case 'fiber':
        currentValue = latest.fiberPercentage;
        break;
      case 'moisture':
        currentValue = latest.moisturePercentage;
        break;
      
      // Major minerals
      case 'calcium':
        currentValue = latest.calciumMg || 0;
        break;
      case 'phosphorus':
        currentValue = latest.phosphorusMg || 0;
        break;
      case 'magnesium':
        currentValue = latest.magnesiumMg || 0;
        break;
      case 'potassium':
        currentValue = latest.potassiumMg || 0;
        break;
      case 'sodium':
        currentValue = latest.sodiumMg || 0;
        break;
      
      // Trace minerals
      case 'iron':
        currentValue = latest.ironMg || 0;
        break;
      case 'zinc':
        currentValue = latest.zincMg || 0;
        break;
      case 'manganese':
        currentValue = latest.manganeseMg || 0;
        break;
      case 'copper':
        currentValue = latest.copperMg || 0;
        break;
      case 'selenium':
        currentValue = latest.seleniumMg || 0;
        break;
      
      // Fat-soluble vitamins
      case 'vitaminA':
        currentValue = latest.vitaminA_IU || 0;
        break;
      case 'vitaminD3':
        currentValue = latest.vitaminD3_IU || 0;
        break;
      case 'vitaminE':
        currentValue = latest.vitaminE_IU || 0;
        break;
      case 'vitaminK':
        currentValue = latest.vitaminK_mg || 0;
        break;
      
      // Water-soluble vitamins
      case 'thiamine':
        currentValue = latest.thiamine_mg || 0;
        break;
      case 'riboflavin':
        currentValue = latest.riboflavin_mg || 0;
        break;
      case 'niacin':
        currentValue = latest.niacin_mg || 0;
        break;
      case 'pantothenicAcid':
        currentValue = latest.pantothenicAcid_mg || 0;
        break;
      case 'pyridoxine':
        currentValue = latest.pyridoxine_mg || 0;
        break;
      case 'biotin':
        currentValue = latest.biotin_mg || 0;
        break;
      case 'folicAcid':
        currentValue = latest.folicAcid_mg || 0;
        break;
      case 'vitaminB12':
        currentValue = latest.vitaminB12_mg || 0;
        break;
      case 'choline':
        currentValue = latest.choline_mg || 0;
        break;
      
      // Quality and cost
      case 'quality':
        currentValue = latest.qualityScore;
        break;
      // Cost would need additional data
    }

    // Calculate progress based on target range if available
    if (goal.metadata?.targetRange) {
      const { min, max, optimal } = goal.metadata.targetRange;
      if (currentValue >= min && currentValue <= max) {
        // Within acceptable range, calculate progress based on distance from optimal
        const distanceFromOptimal = Math.abs(currentValue - optimal);
        const acceptableRange = max - min;
        const progress = Math.max(70, 100 - (distanceFromOptimal / acceptableRange) * 30);
        return { currentValue, progress };
      } else if (currentValue < min) {
        // Below minimum
        const deficit = min - currentValue;
        const progress = Math.max(0, 70 - (deficit / min) * 70);
        return { currentValue, progress };
      } else {
        // Above maximum
        const excess = currentValue - max;
        const progress = Math.max(0, 70 - (excess / max) * 70);
        return { currentValue, progress };
      }
    }

    // Fallback to baseline calculation
    const baseline = goal.metadata?.baseline || 0;
    const target = goal.targetValue;
    const progress = Math.min(100, Math.max(0, 
      ((currentValue - baseline) / (target - baseline)) * 100
    ));

    return { currentValue, progress };
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      // Macronutrients
      case 'protein':
        return <BeakerIcon className="h-5 w-5" />;
      case 'fat':
        return <FireIcon className="h-5 w-5" />;
      case 'fiber':
      case 'moisture':
        return <CubeIcon className="h-5 w-5" />;
      
      // Major minerals
      case 'calcium':
      case 'phosphorus':
      case 'magnesium':
      case 'potassium':
      case 'sodium':
        return <AdjustmentsHorizontalIcon className="h-5 w-5" />;
      
      // Trace minerals
      case 'iron':
      case 'zinc':
      case 'manganese':
      case 'copper':
      case 'selenium':
        return <SparklesIcon className="h-5 w-5" />;
      
      // Vitamins
      case 'vitaminA':
      case 'vitaminD3':
      case 'vitaminE':
      case 'vitaminK':
      case 'thiamine':
      case 'riboflavin':
      case 'niacin':
      case 'pantothenicAcid':
      case 'pyridoxine':
      case 'biotin':
      case 'folicAcid':
      case 'vitaminB12':
      case 'choline':
        return <LightBulbIcon className="h-5 w-5" />;
      
      // Quality and cost
      case 'quality':
        return <StarIcon className="h-5 w-5" />;
      case 'cost':
        return <CurrencyDollarIcon className="h-5 w-5" />;
      default:
        return <ChartBarIcon className="h-5 w-5" />;
    }
  };

  const filteredGoals = goals.filter(goal => {
    if (filter === 'all') return true;
    return goal.status === filter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'achieved':
        return 'text-green-600 bg-green-100';
      case 'active':
        return 'text-cyan-600 bg-blue-100';
      case 'overdue':
        return 'text-red-600 bg-red-100';
      case 'paused':
        return 'text-slate-600 bg-gray-100';
      default:
        return 'text-slate-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-slate-600';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 70) return 'bg-cyan-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const handleCreateGoal = () => {
    const goal: NutritionalGoal = {
      id: Date.now().toString(),
      ...newGoal,
      currentValue: 0,
      status: 'active',
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setGoals(prev => [...prev, goal]);
    setShowCreateModal(false);
    setNewGoal({
      name: '',
      description: '',
      category: 'protein',
      targetValue: 0,
      unit: '%',
      targetDate: '',
      priority: 'medium',
      locations: []
    });
  };

  const achievedCount = goals.filter(g => g.status === 'achieved').length;
  const activeCount = goals.filter(g => g.status === 'active').length;
  const overdueCount = goals.filter(g => g.status === 'overdue').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <TagIcon className="h-6 w-6 text-cyan-600 mr-2" />
            <h2 className="text-lg font-medium text-slate-900">Nutritional Goals</h2>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Goal
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{achievedCount}</div>
            <div className="text-sm text-slate-600">Achieved</div>
          </div>
          <div className="text-center p-4 bg-cyan-50 rounded-lg">
            <div className="text-2xl font-bold text-cyan-600">{activeCount}</div>
            <div className="text-sm text-slate-600">Active</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{overdueCount}</div>
            <div className="text-sm text-slate-600">Overdue</div>
          </div>
          <div className="text-center p-4 bg-slate-50/80 rounded-lg">
            <div className="text-2xl font-bold text-slate-600">{goals.length}</div>
            <div className="text-sm text-slate-600">Total</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex space-x-2">
          {['all', 'active', 'achieved', 'overdue'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${
                filter === status
                  ? 'bg-cyan-100 text-cyan-700'
                  : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Goals List */}
      <div className="space-y-4">
        {filteredGoals.map((goal) => {
          const updated = trendData.length > 0 ? updateGoalProgress(goal, trendData) : { currentValue: goal.currentValue, progress: goal.progress };
          
          return (
            <div key={goal.id} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <div className="flex items-center mr-3">
                      <div className="p-2 bg-gray-100 rounded-lg mr-3">
                        {getCategoryIcon(goal.category)}
                      </div>
                      <h3 className="text-lg font-medium text-slate-900">{goal.name}</h3>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                      {goal.status}
                    </span>
                    <span className={`ml-2 text-sm font-medium ${getPriorityColor(goal.priority)}`}>
                      {goal.priority} priority
                    </span>
                  </div>
                  
                  {goal.description && (
                    <p className="text-sm text-slate-600 mb-3">{goal.description}</p>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Current:</span>
                      <span className="ml-1 font-medium">{updated.currentValue.toFixed(1)} {goal.unit}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Target:</span>
                      <span className="ml-1 font-medium">{goal.targetValue} {goal.unit}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Due:</span>
                      <span className="ml-1 font-medium">{formatDate(new Date(goal.targetDate))}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Progress:</span>
                      <span className="ml-1 font-medium">{updated.progress.toFixed(0)}%</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-500">Progress</span>
                      <span className="text-xs text-slate-900">{updated.progress.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(updated.progress)}`}
                        style={{ width: `${Math.min(100, updated.progress)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Milestones */}
                  {goal.metadata?.milestones && goal.metadata.milestones.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-slate-900 mb-2">Recent Milestones</h4>
                      <div className="space-y-1">
                        {goal.metadata.milestones.slice(-3).map((milestone, index) => (
                          <div key={index} className="flex items-center text-xs text-slate-600">
                            <CheckCircleIcon className="h-3 w-3 text-green-500 mr-2" />
                            <span>{formatDate(new Date(milestone.date))}: {milestone.value}{goal.unit}</span>
                            {milestone.note && <span className="ml-2 text-slate-500">({milestone.note})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => setSelectedGoal(goal)}
                    className="p-2 text-slate-400 hover:text-slate-500"
                  >
                    <ChartBarIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setEditGoal(goal); setShowEditModal(true); }}
                    className="p-2 text-slate-400 hover:text-slate-500"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setDeleteGoal(goal); setShowDeleteConfirm(true); }}
                    className="p-2 text-slate-400 hover:text-red-500"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredGoals.length === 0 && (
        <div className="text-center py-12 bg-white shadow rounded-lg">
          <TagIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-900">No goals found</h3>
          <p className="mt-1 text-sm text-slate-500">
            {filter === 'all' ? 'Create your first nutritional goal to get started' : `No ${filter} goals found`}
          </p>
        </div>
      )}

      {/* Create Goal Modal (simplified) */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 modal-overlay" onClick={() => setShowCreateModal(false)}></div>
            <div className="modal-shell inline-block align-bottom text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-10">
              <div className="modal-header flex items-start justify-between gap-4">
                <h3 className="gradient-title text-lg font-semibold">Create New Goal</h3>
                <ModalCloseButton onClose={() => setShowCreateModal(false)} />
              </div>
              <div className="modal-body">
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Goal Name</label>
                    <input
                      type="text"
                      value={newGoal.name}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Category</label>
                    <select
                      value={newGoal.category}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, category: e.target.value as any }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <optgroup label="Macronutrients">
                        <option key="protein-1753461297331" value="protein">Protein</option>
                        <option key="fat-1753461297331" value="fat">Fat</option>
                        <option key="fiber-1753461297331" value="fiber">Fiber</option>
                        <option key="moisture-1753461297331" value="moisture">Moisture</option>
                      </optgroup>
                      <optgroup label="Major Minerals">
                        <option key="calcium-1753461297331" value="calcium">Calcium</option>
                        <option key="phosphorus-1753461297331" value="phosphorus">Phosphorus</option>
                        <option key="magnesium-1753461297331" value="magnesium">Magnesium</option>
                        <option key="potassium-1753461297331" value="potassium">Potassium</option>
                        <option key="sodium-1753461297331" value="sodium">Sodium</option>
                      </optgroup>
                      <optgroup label="Trace Minerals">
                        <option key="iron-1753461297331" value="iron">Iron</option>
                        <option key="zinc-1753461297331" value="zinc">Zinc</option>
                        <option key="manganese-1753461297331" value="manganese">Manganese</option>
                        <option key="copper-1753461297331" value="copper">Copper</option>
                        <option key="selenium-1753461297331" value="selenium">Selenium</option>
                      </optgroup>
                      <optgroup label="Vitamins">
                        <option key="vitaminA-1753461297331" value="vitaminA">Vitamin A</option>
                        <option key="vitaminD3-1753461297331" value="vitaminD3">Vitamin D3</option>
                        <option key="vitaminE-1753461297331" value="vitaminE">Vitamin E</option>
                        <option key="vitaminK-1753461297331" value="vitaminK">Vitamin K</option>
                        <option key="thiamine-1753461297331" value="thiamine">Thiamine (B1)</option>
                        <option key="riboflavin-1753461297331" value="riboflavin">Riboflavin (B2)</option>
                        <option key="niacin-1753461297331" value="niacin">Niacin (B3)</option>
                        <option key="pantothenicAcid-1753461297331" value="pantothenicAcid">Pantothenic Acid (B5)</option>
                        <option key="pyridoxine-1753461297331" value="pyridoxine">Pyridoxine (B6)</option>
                        <option key="biotin-1753461297331" value="biotin">Biotin</option>
                        <option key="folicAcid-1753461297331" value="folicAcid">Folic Acid</option>
                        <option key="vitaminB12-1753461297331" value="vitaminB12">Vitamin B12</option>
                        <option key="choline-1753461297331" value="choline">Choline</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option key="quality-1753461297331" value="quality">Quality Score</option>
                        <option key="cost-1753461297331" value="cost">Cost</option>
                        <option key="custom-1753461297331" value="custom">Custom</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <DecimalInput
                        label="Target Value"
                        value={newGoal.targetValue}
                        onChange={(value) => setNewGoal((prev) => ({ ...prev, targetValue: value ?? 0 }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Target Date</label>
                      <input
                        type="date"
                        value={newGoal.targetDate}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, targetDate: e.target.value }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">Priority</label>
                    <select
                      value={newGoal.priority}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="high-1753461297331" value="high">High</option>
                      <option key="medium-1753461297331" value="medium">Medium</option>
                      <option key="low-1753461297331" value="low">Low</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateGoal}
                  disabled={!newGoal.name.trim() || !newGoal.targetValue || !newGoal.targetDate}
                  className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Goal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Goal Modal */}
      {showEditModal && editGoal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 modal-overlay" onClick={() => setShowEditModal(false)}></div>
            <div className="modal-shell inline-block align-bottom text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-10">
              <div className="modal-header flex items-start justify-between gap-4">
                <h3 className="gradient-title text-lg font-semibold">Edit Goal</h3>
                <ModalCloseButton onClose={() => { setShowEditModal(false); setEditGoal(null); }} />
              </div>
              <div className="modal-body">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Goal Name</label>
                    <input
                      type="text"
                      value={editGoal.name}
                      onChange={(e) => setEditGoal(prev => prev ? { ...prev, name: e.target.value } : null)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Category</label>
                    <select
                      value={editGoal.category}
                      onChange={(e) => setEditGoal(prev => prev ? { ...prev, category: e.target.value as any } : null)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <optgroup label="Macronutrients">
                        <option key="protein-1753461297331" value="protein">Protein</option>
                        <option key="fat-1753461297331" value="fat">Fat</option>
                        <option key="fiber-1753461297331" value="fiber">Fiber</option>
                        <option key="moisture-1753461297331" value="moisture">Moisture</option>
                      </optgroup>
                      <optgroup label="Major Minerals">
                        <option key="calcium-1753461297331" value="calcium">Calcium</option>
                        <option key="phosphorus-1753461297331" value="phosphorus">Phosphorus</option>
                        <option key="magnesium-1753461297331" value="magnesium">Magnesium</option>
                        <option key="potassium-1753461297331" value="potassium">Potassium</option>
                        <option key="sodium-1753461297331" value="sodium">Sodium</option>
                      </optgroup>
                      <optgroup label="Trace Minerals">
                        <option key="iron-1753461297331" value="iron">Iron</option>
                        <option key="zinc-1753461297331" value="zinc">Zinc</option>
                        <option key="manganese-1753461297331" value="manganese">Manganese</option>
                        <option key="copper-1753461297331" value="copper">Copper</option>
                        <option key="selenium-1753461297331" value="selenium">Selenium</option>
                      </optgroup>
                      <optgroup label="Vitamins">
                        <option key="vitaminA-1753461297331" value="vitaminA">Vitamin A</option>
                        <option key="vitaminD3-1753461297331" value="vitaminD3">Vitamin D3</option>
                        <option key="vitaminE-1753461297331" value="vitaminE">Vitamin E</option>
                        <option key="vitaminK-1753461297331" value="vitaminK">Vitamin K</option>
                        <option key="thiamine-1753461297331" value="thiamine">Thiamine (B1)</option>
                        <option key="riboflavin-1753461297331" value="riboflavin">Riboflavin (B2)</option>
                        <option key="niacin-1753461297331" value="niacin">Niacin (B3)</option>
                        <option key="pantothenicAcid-1753461297331" value="pantothenicAcid">Pantothenic Acid (B5)</option>
                        <option key="pyridoxine-1753461297331" value="pyridoxine">Pyridoxine (B6)</option>
                        <option key="biotin-1753461297331" value="biotin">Biotin</option>
                        <option key="folicAcid-1753461297331" value="folicAcid">Folic Acid</option>
                        <option key="vitaminB12-1753461297331" value="vitaminB12">Vitamin B12</option>
                        <option key="choline-1753461297331" value="choline">Choline</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option key="quality-1753461297331" value="quality">Quality Score</option>
                        <option key="cost-1753461297331" value="cost">Cost</option>
                        <option key="custom-1753461297331" value="custom">Custom</option>
                      </optgroup>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <DecimalInput
                        label="Target Value"
                        value={editGoal.targetValue}
                        onChange={(value) => setEditGoal((prev) => (prev ? { ...prev, targetValue: value ?? 0 } : null))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Target Date</label>
                      <input
                        type="date"
                        value={editGoal.targetDate}
                        onChange={(e) => setEditGoal(prev => prev ? { ...prev, targetDate: e.target.value } : null)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Priority</label>
                    <select
                      value={editGoal.priority}
                      onChange={(e) => setEditGoal(prev => prev ? { ...prev, priority: e.target.value as any } : null)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="high-1753461297331" value="high">High</option>
                      <option key="medium-1753461297331" value="medium">Medium</option>
                      <option key="low-1753461297331" value="low">Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditGoal(null); }}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGoals(prev => prev.map(g => g.id === editGoal.id ? { ...editGoal, updatedAt: new Date().toISOString() } : g));
                    setShowEditModal(false);
                    setEditGoal(null);
                  }}
                  disabled={!editGoal.name.trim() || !editGoal.targetValue || !editGoal.targetDate}
                  className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Goal Confirmation Dialog */}
      {showDeleteConfirm && deleteGoal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 modal-overlay" onClick={() => setShowDeleteConfirm(false)}></div>
            <div className="modal-shell inline-block align-bottom text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full relative z-10">
              <div className="modal-header flex items-start justify-between gap-4">
                <h3 className="gradient-title text-lg font-semibold">Delete Goal</h3>
                <ModalCloseButton onClose={() => { setShowDeleteConfirm(false); setDeleteGoal(null); }} />
              </div>
              <div className="modal-body">
                <p className="text-sm text-slate-600">Are you sure you want to delete the goal <span className="font-semibold">{deleteGoal.name}</span>? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteGoal(null); }}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGoals(prev => prev.filter(g => g.id !== deleteGoal.id));
                    setShowDeleteConfirm(false);
                    setDeleteGoal(null);
                  }}
                  className="btn btn-danger text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 